import { Component, computed, OnInit } from '@angular/core';
import { MatCard, MatCardContent } from '@angular/material/card';
import { combineLatest, take } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DynamicObservable } from '../../../../core/classes/dynamic-observable';
import { DatatableComponent } from '../../../../shared/components/datatable/datatable.component';
import {
  ETableCrudScope,
  IDatatableColumn,
  IDatatableConfiguration,
} from '../../../../shared/components/datatable/datatable.interface';
import { IBaseDatatableState } from '../../../../state/base-datatable-store.service';
import { FormUtilitiesService } from '../../../../utilities/form-utilities.service';
import { BaseCrudPageComponent } from '../../../app/base-crud-page.component';
import { WidgetRequestsAddEditDialogComponent } from './widget-requests-add-edit-dialog/widget-requests-add-edit-dialog.component';
import { IWidgetRequestsAddEditDialogData } from './widget-requests-add-edit-dialog/widget-requests-add-edit-dialog.interface';
import { IWidgetDatatableRow, IWidgetRequest } from './widget-requests.interface';
import { WidgetRequestStore } from './widget-requests.store';

@Component({
  imports: [DatatableComponent, MatCard, MatCardContent],
  providers: [WidgetRequestStore],
  selector: 'app-widget-requests',
  styleUrl: './widget-requests.component.scss',
  templateUrl: './widget-requests.component.html',
})
export class WidgetRequestsComponent
  extends BaseCrudPageComponent<IWidgetRequest, IBaseDatatableState<IWidgetRequest>>
  implements OnInit
{
  public readonly formattedDatatableRows = computed(() => {
    return this.store.data$().map(({ id, name, dataSource }) => ({
      dataSource: dataSource.name,
      id,
      name,
    }));
  });

  public readonly datatableColumns: IDatatableColumn<IWidgetDatatableRow>[] = [
    { id: 'id', isSortable: false },
    { id: 'name', isSortable: true },
    { id: 'dataSource', isSortable: true },
  ];

  public datatableConfigurations: IDatatableConfiguration<IWidgetDatatableRow> = {
    addButtonText: this.addText,
    addScope: ETableCrudScope.single,
    dataRefreshEvent: this.setPageToOneAndRefreshDatatable$,
    deleteScope: ETableCrudScope.bulk,
    editScope: ETableCrudScope.single,
  };

  private readonly afterCreateUpdateSuccess = new DynamicObservable<unknown>(null);

  constructor(
    public readonly store: WidgetRequestStore,
    public readonly formService: FormUtilitiesService,
  ) {
    super(store, {
      ambiguousNumberContext: 'field.ambiguousNumberOfWidgetRequest',
      multiContext: 'field.endpoints',
      nameProperty: 'name',
      singleContext: 'field.endpoint',
    });
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      combineLatest({
        afterCreateUpdateSuccess: this.afterCreateUpdateSuccess.obs$,
        datatable: this.datatableParams$.pipe(filter(Boolean)),
      }).subscribe(({ datatable }) => {
        this.store.loadWidgetRequests({
          fields: ['content', 'dataSourceId', 'endpoint', 'name', 'requestMethod', 'authorization', 'resultType'],
          join: ['dataSource||name'],
          limit: datatable.limit,
          page: datatable.page,
          sort: [datatable.sort],
          ...(datatable.search ? { search: { searchedFields: ['name'], searchText: datatable.search } } : {}),
        });
      }),
    );
  }

  public initializeAddEdit(isEdit: boolean): void {
    if (isEdit) {
      this.setupForEdit();
    } else {
      this.setupForAdd();
    }

    const data: IWidgetRequestsAddEditDialogData = {
      dialogTitle: this.addEditModalTitle(),
      submitButtonText: this.addEditSubmitButtonText(),
      ...(this.isEditDialog()
        ? {
            editData: this.selectedRows()[0],
          }
        : {}),
    };

    this.afterCreateUpdateSuccess.switch(
      this.matDialog
        .open(WidgetRequestsAddEditDialogComponent, {
          ...WidgetRequestsAddEditDialogComponent.dialogConfig,
          data,
        })
        .afterClosed()
        .pipe(take(1)),
    );
  }
}
